import { FC, useEffect, useState } from 'react'
import { Box, CircularProgress, DialogContent, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import theme from 'theme'
import ThemeButton from 'ui/components/buttons/theme/Button'
import TextFieldV2 from 'ui/components/form-components-v2/text-field-v2'
import { OnyxiaTooltip } from 'ui/components/tooltip'
import useDebounce from 'hooks/useDebounce'
import { ReportTemplateService } from 'services'
import { CustomizedDialog } from '../customize-report-dialog/customizeReportDialog.styles'

interface DuplicateTemplateDialogProps {
  isOpen: boolean
  templateName: string
  onClose: () => void
  onConfirm: (name: string) => Promise<void>
}

const DuplicateTemplateDialog: FC<DuplicateTemplateDialogProps> = ({ isOpen, templateName, onClose, onConfirm }) => {
  const [name, setName] = useState('')
  const [isDuplicating, setIsDuplicating] = useState(false)
  const [isNameTaken, setIsNameTaken] = useState(false)
  const debouncedName = useDebounce(name, 400)

  useEffect(() => {
    if (isOpen) {
      setName(`(Copy) ${templateName}`.slice(0, 50))
      setIsNameTaken(false)
    }
  }, [isOpen, templateName])

  useEffect(() => {
    if (!isOpen) return
    const trimmed = debouncedName.trim()
    if (!trimmed) {
      setIsNameTaken(false)
      return
    }
    let cancelled = false
    ReportTemplateService.checkNameExists(trimmed)
      .then((res) => {
        if (!cancelled) setIsNameTaken(res.exists)
      })
      .catch(() => {
        if (!cancelled) setIsNameTaken(false)
      })
    return () => {
      cancelled = true
    }
  }, [debouncedName, isOpen])

  const handleConfirm = async () => {
    if (!name.trim() || isNameTaken) return
    try {
      setIsDuplicating(true)
      await onConfirm(name)
    } finally {
      setIsDuplicating(false)
    }
  }

  const duplicateButton = (
    <ThemeButton
      themevariant="primary"
      onClick={handleConfirm}
      disabled={isDuplicating || !name.trim() || isNameTaken}
      sx={{ flex: 1, height: '48px' }}
    >
      {isDuplicating ? <CircularProgress size={18} color="primary" /> : 'Duplicate'}
    </ThemeButton>
  )

  const nameTakenTooltipTitle = (
    <Box>
      <Typography
        sx={{
          fontFamily: theme.typography.fontFamily.primary,
          fontSize: theme.typography.fontSize.medium,
          fontWeight: theme.typography.fontWeight.extraThick,
          lineHeight: '140%',
          color: theme.colors.menuItemText,
          mb: '12px'
        }}
      >
        Duplication not available
      </Typography>
      <Typography
        sx={{
          fontSize: theme.typography.fontSize.medium,
          color: theme.colors.devicesSecondaryText,
          lineHeight: '20px'
        }}
      >
        A template with this name already exists. Please choose a different name to continue.
      </Typography>
    </Box>
  )

  return (
    <CustomizedDialog
      open={isOpen}
      onClose={onClose}
      aria-labelledby="duplicate-template-dialog"
      maxWidth={false}
      sx={{
        '.MuiDialog-paper': {
          width: '664px !important',
          padding: '32px !important',
          minHeight: 'auto'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', width: '100%', mb: '24px' }}>
        <Typography
          sx={{
            color: theme.colors.menuItemText,
            textAlign: 'center',
            fontFamily: theme.typography.fontFamily.primary,
            fontSize: '28px',
            fontWeight: theme.typography.fontWeight.bold,
            lineHeight: '42px',
            flex: '1 0 0'
          }}
        >
          Duplicate Template &lsquo;{templateName}&rsquo;
        </Typography>
        <IconButton onClick={onClose} sx={{ color: theme.colors.devicesSecondaryText }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 0, width: '100%' }}>
        <Typography
          sx={{
            fontSize: theme.typography.fontSize.large,
            color: theme.colors.devicesSecondaryText,
            textAlign: 'center',
            mb: '32px'
          }}
        >
          Creates a copy in your Templates. You can rename it now or later.
        </Typography>
        <TextFieldV2
          id="duplicate-template-name"
          label="Report name"
          variant="outlined"
          placeholder="Enter report name"
          autoComplete="off"
          maxLength={50}
          InputLabelProps={{ shrink: true }}
          value={name}
          onChange={(e: any) => setName(e.target.value)}
        />
      </DialogContent>
      <Box sx={{ display: 'flex', gap: 1, width: '100%', mt: '48px' }}>
        <ThemeButton themevariant="secondary" onClick={onClose} disabled={isDuplicating} sx={{ flex: 1, height: '48px' }}>
          Cancel
        </ThemeButton>
        {isNameTaken ? (
          <OnyxiaTooltip
            arrow
            placement="top-end"
            title={nameTakenTooltipTitle as unknown as string}
            componentsProps={{ tooltip: { sx: { width: '199px', maxWidth: '199px' } } }}
          >
            <Box sx={{ flex: 1, display: 'flex' }}>{duplicateButton}</Box>
          </OnyxiaTooltip>
        ) : (
          duplicateButton
        )}
      </Box>
    </CustomizedDialog>
  )
}

export default DuplicateTemplateDialog
